import { useLayoutEffect, useMemo, useState } from 'react';

/** Chakra default breakpoints (16px root): md 48em, lg 62em, xl 80em */
const VIEWPORT_MD_MIN_PX = 768;
const VIEWPORT_LG_MIN_PX = 992;
const VIEWPORT_XL_MIN_PX = 1280;

export function parseThemeSizeToPx(value: string): number {
  const v = value.trim();
  if (!v) return 0;
  if (v.endsWith('px')) return Math.max(0, Number.parseFloat(v));
  if (v.endsWith('rem')) return Math.max(0, Number.parseFloat(v)) * 16;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

/**
 * Inline start inset for the ATF carousel: matches `carouselInlineStartOffset` CSS —
 * `0` / `24px` / `48px` / `max(0, (100vw - contentMax) / 2)` at xl+.
 */
export function getExperienceAtfCarouselStartInsetPx(viewportWidthPx: number, contentMaxWidthPx: number): number {
  if (viewportWidthPx < VIEWPORT_MD_MIN_PX) return 0;
  if (viewportWidthPx < VIEWPORT_LG_MIN_PX) return 24;
  if (viewportWidthPx < VIEWPORT_XL_MIN_PX) return 48;
  return Math.max(0, (viewportWidthPx - contentMaxWidthPx) / 2);
}

/**
 * Pixel inset for Swiper `slidesOffsetBefore` (and parity checks). Updates on resize so xl+ tracks
 * `max(0, (100vw - content-max-width-lg) / 2)` dynamically.
 */
export function useExperienceAtfCarouselStartInsetPx(contentMaxWidthToken: string): number {
  const contentMaxWidthPx = useMemo(() => parseThemeSizeToPx(contentMaxWidthToken), [contentMaxWidthToken]);
  const [insetPx, setInsetPx] = useState(0);

  useLayoutEffect(() => {
    const update = () => {
      setInsetPx(getExperienceAtfCarouselStartInsetPx(window.innerWidth, contentMaxWidthPx));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [contentMaxWidthPx]);

  return insetPx;
}
